"use client";

/**
 * Calendar View
 * Renders schedule items in a time-grid weekly calendar layout.
 *
 * - Only days that have items become columns (empty days are never rendered).
 * - Desktop (`md` viewport ≥ 768px): up to SCHEDULE_CALENDAR_MAX_DAYS_PER_PAGE columns
 *   visible at once; older pages reached via prev/next pagination.
 * - Mobile (< `md`): horizontal day-chip strip above a single-day time grid.
 * - Items without a start_time are shown in an "All day" row above the time grid.
 * - Items without a date are shown in a separate "Unscheduled" section below.
 */

import type React from "react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { formatDateShort, formatDayNumber, formatDayOfWeek, formatDayOfWeekShort } from "@/lib/utils/formatting";
import { getButtonTextColor } from "@/features/landing-pages/_logic/color-utils";
import {
  SCHEDULE_CALENDAR_DESKTOP_HOUR_HEIGHT_PX,
  SCHEDULE_CALENDAR_DESKTOP_MAX_VIEWPORT_PX,
  SCHEDULE_CALENDAR_HOUR_HEIGHT_PX,
  SCHEDULE_CALENDAR_MAX_DAYS_PER_PAGE,
  SCHEDULE_CALENDAR_MOBILE_VIEWPORT_PX,
  SCHEDULE_HOUR_SCALE_DEFAULT,
  SCHEDULE_HOUR_SCALE_MAX,
  SCHEDULE_HOUR_SCALE_MIN,
} from "../../../../_logic/constants";
import { usePinchZoom } from "../../../../_hooks/usePinchZoom";
import type { ScheduleItemRow, ScheduleLabelRow } from "../../../../_logic/types/db-types";
import {
  computeHourRange,
  hoursInRange,
  isTimedItem,
  layoutDayItems,
  type DatedGroup,
  type TimedEntry,
} from "../../../../_logic/utils/schedule-calendar-layout";
import { minuteOfDayInTimeZone } from "../../../../_logic/utils/schedule-utils";
import { scheduleNow } from "../../../../_logic/utils/schedule-clock";
import { useEventToday } from "../../../../_hooks/useEventToday";
import { CalendarEventCard } from "./CalendarEventCard";
import { CalendarDayColumn, CalendarHourGutter, HOUR_LABEL_WIDTH_PX } from "./CalendarTimeGrid";
import { DayChip } from "./DayChip";
import { getFirstLabelColor } from "./label-utils";
import {
  getScheduleItemRegistrationState,
  type ViewerRegistrationKeys,
} from "../../../../_logic/utils/schedule-registration-match";
import { isScheduleItemSignupClosed } from "../../../../_logic/utils/activity-signup-status";

interface CalendarViewProps {
  dateGroups: { date: string | null; items: ScheduleItemRow[] }[];
  /** Pre-computed label lookup map (computed once in ScheduleRenderer) */
  labelMap: Map<number, ScheduleLabelRow>;
  primaryColor: string;
  onDelete?: (item: ScheduleItemRow) => void;
  /** When provided, items become clickable (used in public view for detail modal) */
  onItemClick?: (item: ScheduleItemRow) => void;
  /** Page-wide IANA timezone for rendering times. NULL = floating time. */
  timeZone: string | null;
  isPublic?: boolean;
  /** Viewer's status-bucketed registrations. `null` = no logged-in attendee. */
  viewerRegistrations: ViewerRegistrationKeys | null;
  /** Whether to show the hour/time-label gutter. Defaults to true (current behavior). */
  showTimeLabels?: boolean;
  /**
   * Optional grid-height overrides. Both default to the inline-section constants
   * (SCHEDULE_CALENDAR_DESKTOP_MAX_VIEWPORT_PX / SCHEDULE_CALENDAR_MOBILE_VIEWPORT_PX); the focused
   * full-page view passes a measured viewport height for each so the grid fills the screen.
   */
  viewport?: {
    /** Max height (px) of the desktop time-grid scroll container. */
    desktopMaxHeight?: number;
    /** Fixed height (px) of the mobile single-day time-grid viewport. */
    mobileViewportPx?: number;
    /** Fill the parent height and keep vertical scrolling inside the calendar grid. */
    fillAvailable?: boolean;
  };
}

/** Per-day pre-computed derived data so map() callbacks don't recompute on every render. */
interface DayDerived {
  allDayItems: ScheduleItemRow[];
  timedEntries: TimedEntry[];
  weekday: string;
  weekdayShort: string;
  dateShort: string;
  dayNumber: string;
}

type ScrollEdge = "top" | "middle" | "bottom" | "none";

function getScrollEdge(el: HTMLElement): ScrollEdge {
  const overflow = el.scrollHeight - el.clientHeight;
  if (overflow <= 1) return "none";
  if (el.scrollTop <= 1) return "top";
  if (el.scrollTop >= overflow - 1) return "bottom";
  return "middle";
}

function clampScrollTop(el: HTMLElement, nextScrollTop: number): number {
  const maxScrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
  return Math.min(maxScrollTop, Math.max(0, nextScrollTop));
}

/** Renders the calendar view: paginated time-grid of dated items + an unscheduled section. */
export function CalendarView({
  dateGroups,
  labelMap,
  primaryColor,
  onDelete,
  onItemClick,
  timeZone,
  isPublic = false,
  viewerRegistrations,
  viewport,
  showTimeLabels = true,
}: CalendarViewProps): React.ReactElement | null {
  const desktopMaxHeight = viewport?.desktopMaxHeight ?? SCHEDULE_CALENDAR_DESKTOP_MAX_VIEWPORT_PX;
  const mobileViewportPx = viewport?.mobileViewportPx ?? SCHEDULE_CALENDAR_MOBILE_VIEWPORT_PX;
  const fillAvailable = viewport?.fillAvailable ?? false;
  const isItemRegistered = (item: ScheduleItemRow): boolean =>
    getScheduleItemRegistrationState(item, viewerRegistrations) === "registered";
  const isItemWaitlisted = (item: ScheduleItemRow): boolean =>
    getScheduleItemRegistrationState(item, viewerRegistrations) === "waitlisted";
  const isItemSignupClosed = (item: ScheduleItemRow): boolean => isScheduleItemSignupClosed(item);
  const canItemSignUp = (item: ScheduleItemRow): boolean =>
    item.activity_id != null &&
    viewerRegistrations != null &&
    getScheduleItemRegistrationState(item, viewerRegistrations) == null &&
    !isScheduleItemSignupClosed(item);
  const mobileBranchClass = isPublic ? "md:hidden" : "@md:hidden";
  const desktopBranchClass = isPublic ? "md:flex" : "@md:flex";
  const unscheduledGridClass = isPublic ? "sm:grid-cols-2 md:grid-cols-3" : "@sm:grid-cols-2 @md:grid-cols-3";
  const activeChipTextColor = getButtonTextColor(primaryColor);
  // States
  const [pageIndex, setPageIndex] = useState(0);
  const [mobileSelectedDate, setMobileSelectedDate] = useState<string | null>(null);
  // Desktop day focus: null = full week ("All"); a date = that single day rendered full-width.
  const [desktopSelectedDate, setDesktopSelectedDate] = useState<string | null>(null);
  const [mobileScrollEdge, setMobileScrollEdge] = useState<ScrollEdge>("none");
  const [measuredMobileViewportPx, setMeasuredMobileViewportPx] = useState<number | null>(null);
  const [measuredMobileChromePx, setMeasuredMobileChromePx] = useState(0);
  const [didInitToday, setDidInitToday] = useState(false);
  // Today's date in the event zone, resolved on mount. Drives which single column mounts the "now"
  // line, so only that column ticks (instead of one timer per visible day). Null until mounted.
  const todayString = useEventToday(timeZone);
  // The date we last auto-scrolled the mobile grid to "now" for. Keeps the scroll-to-now effect
  // firing once per today-selection so it doesn't fight the user's manual scroll / pinch-zoom.
  const scrolledToNowForDateRef = useRef<string | null>(null);
  const lastMobileScrollDateRef = useRef<string | null>(null);
  const mobileChromeRef = useRef<HTMLDivElement | null>(null);

  // Refs / hooks — pinch-to-zoom on the mobile time grid (Google-Calendar-style hour scaling).
  // `ref` is a callback so listeners re-attach when the conditionally-rendered grid mounts;
  // `nodeRef` lets the scroll-edge effect read the DOM node without racing the ref-attach.
  const {
    ref: mobileGridRef,
    nodeRef: mobileGridNodeRef,
    scale: hourScale,
  } = usePinchZoom<HTMLDivElement>({
    min: SCHEDULE_HOUR_SCALE_MIN,
    max: SCHEDULE_HOUR_SCALE_MAX,
    initial: SCHEDULE_HOUR_SCALE_DEFAULT,
    anchorScroll: true,
  });
  const mobileViewportRef = useRef<HTMLDivElement | null>(null);

  // Derived values — separate dated vs undated
  const datedGroups = useMemo<DatedGroup[]>(
    () => dateGroups.filter((g): g is DatedGroup => g.date !== null),
    [dateGroups]
  );
  const undatedGroup = useMemo(() => dateGroups.find((g) => g.date === null) ?? null, [dateGroups]);

  // Derived values — pagination over date groups (chunks of MAX_DAYS_PER_PAGE)
  const totalPages = Math.max(1, Math.ceil(datedGroups.length / SCHEDULE_CALENDAR_MAX_DAYS_PER_PAGE));
  const safePageIndex = Math.min(pageIndex, totalPages - 1);
  const pageStart = safePageIndex * SCHEDULE_CALENDAR_MAX_DAYS_PER_PAGE;
  const visibleGroups = useMemo(
    () => datedGroups.slice(pageStart, pageStart + SCHEDULE_CALENDAR_MAX_DAYS_PER_PAGE),
    [datedGroups, pageStart]
  );

  // Effects — on first load, jump to today (page + mobile day) when today is an event day, so the
  // "now" line is visible and the mobile strip opens on today instead of the first day.
  useEffect(() => {
    if (didInitToday || todayString == null) return;
    if (datedGroups.length === 0) return;
    const idx = datedGroups.findIndex((g) => g.date === todayString);
    if (idx >= 0) {
      setPageIndex(Math.floor(idx / SCHEDULE_CALENDAR_MAX_DAYS_PER_PAGE));
      setMobileSelectedDate(todayString);
    }
    setDidInitToday(true);
  }, [didInitToday, todayString, datedGroups]);

  // Effects — keep the mobile-selected day inside the currently visible page. Gated on
  // `didInitToday` so it can't clobber the today-init selection: both effects run in the same
  // commit when `datedGroups` first populates, and this one would otherwise read the stale
  // `mobileSelectedDate` (null) and overwrite today with the first day.
  useEffect(() => {
    if (!didInitToday) return;
    if (datedGroups.length === 0 || visibleGroups.length === 0) return;
    if (mobileSelectedDate && visibleGroups.some((g) => g.date === mobileSelectedDate)) return;
    setMobileSelectedDate(visibleGroups[0].date);
  }, [didInitToday, datedGroups, visibleGroups, mobileSelectedDate]);

  // Derived values — desktop day focus. `desktopActiveDate` falls back to null (full week) when the
  // selected day isn't on the current page, so paging never leaves a dangling selection. The grid
  // then renders either the whole week or just the focused day at full width.
  const desktopActiveDate =
    desktopSelectedDate && visibleGroups.some((g) => g.date === desktopSelectedDate) ? desktopSelectedDate : null;
  const desktopRenderGroups = useMemo(
    () => (desktopActiveDate ? visibleGroups.filter((g) => g.date === desktopActiveDate) : visibleGroups),
    [desktopActiveDate, visibleGroups]
  );

  // Derived values — desktop hour range covers every timed item on the rendered day(s)
  const { startHour, endHour } = useMemo(() => computeHourRange(desktopRenderGroups), [desktopRenderGroups]);
  const totalHours = endHour - startHour;
  const gridHeight = totalHours * SCHEDULE_CALENDAR_DESKTOP_HOUR_HEIGHT_PX;
  const hours = useMemo(() => hoursInRange(startHour, endHour), [startHour, endHour]);

  // Derived values — pre-compute lane layout + all-day filter + labels per visible day (once per page change)
  const desktopByDate = useMemo<Map<string, DayDerived>>(() => {
    const map = new Map<string, DayDerived>();
    for (const g of visibleGroups) {
      map.set(g.date, {
        allDayItems: g.items.filter((i) => !isTimedItem(i)),
        timedEntries: layoutDayItems(g.items),
        weekday: formatDayOfWeek(g.date),
        weekdayShort: formatDayOfWeekShort(g.date),
        dateShort: formatDateShort(g.date),
        dayNumber: formatDayNumber(g.date),
      });
    }
    return map;
  }, [visibleGroups]);
  const hasAllDay = useMemo(
    () => desktopRenderGroups.some((g) => (desktopByDate.get(g.date)?.allDayItems.length ?? 0) > 0),
    [desktopRenderGroups, desktopByDate]
  );

  // Derived values — mobile (selected single day)
  const mobileGroup = useMemo(
    () => datedGroups.find((g) => g.date === mobileSelectedDate) ?? null,
    [datedGroups, mobileSelectedDate]
  );
  const { startHour: mobileStartHour, endHour: mobileEndHour } = useMemo(
    () => computeHourRange(mobileGroup ? [mobileGroup] : []),
    [mobileGroup]
  );
  const mobileTotalHours = mobileEndHour - mobileStartHour;
  const fallbackMobileViewportPx = Math.max(0, mobileViewportPx - measuredMobileChromePx);
  const effectiveMobileViewportPx = measuredMobileViewportPx ?? fallbackMobileViewportPx;
  // A short day's natural grid (totalHours × base height) can be smaller than the fixed mobile
  // viewport, leaving dead space below the last event. Grow the per-hour height so the day fills
  // the viewport when it would otherwise fall short; longer days keep the base height and scroll.
  // Pinch-zoom (hourScale) still applies on top of this fitted base.
  const mobileFittedHourHeightPx = useMemo(() => {
    if (mobileTotalHours <= 0) return SCHEDULE_CALENDAR_HOUR_HEIGHT_PX;
    const fillHeight = effectiveMobileViewportPx / mobileTotalHours;
    return Math.max(SCHEDULE_CALENDAR_HOUR_HEIGHT_PX, fillHeight);
  }, [mobileTotalHours, effectiveMobileViewportPx]);
  const mobileHourHeightPx = mobileFittedHourHeightPx * hourScale;
  const mobileGridHeight = mobileTotalHours * mobileHourHeightPx;
  const mobileHours = useMemo(() => hoursInRange(mobileStartHour, mobileEndHour), [mobileStartHour, mobileEndHour]);
  const mobileAllDayItems = useMemo(
    () => (mobileGroup ? mobileGroup.items.filter((i) => !isTimedItem(i)) : []),
    [mobileGroup]
  );
  const mobileTimedEntries = useMemo(() => (mobileGroup ? layoutDayItems(mobileGroup.items) : []), [mobileGroup]);
  const mobileGroupHeading = useMemo(
    () => (mobileGroup ? `${formatDayOfWeek(mobileGroup.date)}, ${formatDateShort(mobileGroup.date)}` : ""),
    [mobileGroup]
  );

  // Derived values — accessible page-range announcement (e.g. "Aug 3 to Aug 9")
  const visibleRangeLabel = useMemo(() => {
    if (visibleGroups.length === 0) return "";
    if (visibleGroups.length === 1) return formatDateShort(visibleGroups[0].date);
    const first = formatDateShort(visibleGroups[0].date);
    const last = formatDateShort(visibleGroups[visibleGroups.length - 1].date);
    return `${first} to ${last}`;
  }, [visibleGroups]);

  // Handlers
  const handlePrev = useCallback(() => {
    setPageIndex((p) => Math.max(0, p - 1));
    setDesktopSelectedDate(null);
  }, []);
  const handleNext = useCallback(() => {
    setPageIndex((p) => Math.min(totalPages - 1, p + 1));
    setDesktopSelectedDate(null);
  }, [totalPages]);
  // Track the mobile time-grid scroll position so the fade overlays only render when there's content to scroll to.
  const handleMobileGridScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    setMobileScrollEdge(getScrollEdge(event.currentTarget));
  }, []);

  // Effects — focused mobile keeps the day strip, selected date, and all-day row outside the
  // scroller. Measure that chrome so short-day hour fitting starts from the timed grid's real space.
  useLayoutEffect(() => {
    if (!fillAvailable) {
      setMeasuredMobileChromePx(0);
      return;
    }

    const node = mobileChromeRef.current;
    if (!node) {
      setMeasuredMobileChromePx(0);
      return;
    }

    let raf = 0;
    const measure = (): void => {
      const height = Math.ceil(node.getBoundingClientRect().height);
      setMeasuredMobileChromePx((prev) => (prev === height ? prev : height));
    };
    measure();

    const scheduleMeasure = (): void => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            scheduleMeasure();
          })
        : null;
    observer?.observe(node);
    window.addEventListener("resize", scheduleMeasure);

    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [fillAvailable, mobileSelectedDate, visibleGroups.length, mobileAllDayItems.length]);

  // Effects — measure the actual mobile grid viewport. In focused mode the grid is a flex child
  // rather than a fixed-height box, so this keeps short-day hour sizing aligned with the real space.
  useLayoutEffect(() => {
    const node = mobileViewportRef.current;
    if (!node) {
      setMeasuredMobileViewportPx(null);
      return;
    }

    let raf = 0;
    const measure = (): void => {
      const height = Math.round(node.clientHeight);
      const next = height > 0 ? height : null;
      setMeasuredMobileViewportPx((prev) => (prev === next ? prev : next));
    };
    measure();

    const scheduleMeasure = (): void => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            scheduleMeasure();
          })
        : null;
    observer?.observe(node);
    window.addEventListener("resize", scheduleMeasure);

    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [fillAvailable, mobileSelectedDate, mobileTimedEntries.length]);

  // Effects — keep the mobile grid's internal scroll position deterministic across day switches.
  // A selected day reuses the same DOM scroller, so reset it before paint instead of letting
  // the browser clamp a stale scrollTop from the previous day. Today's day still opens centered
  // on the now-line once per selection; pinch-zoom changes do not reset the user's position.
  useLayoutEffect(() => {
    const el = mobileGridNodeRef.current;
    if (!el) {
      setMobileScrollEdge("none");
      lastMobileScrollDateRef.current = mobileSelectedDate;
      return;
    }

    const selectedDateChanged = lastMobileScrollDateRef.current !== mobileSelectedDate;
    if (selectedDateChanged && mobileSelectedDate !== todayString) {
      scrolledToNowForDateRef.current = null;
    }

    const shouldScrollToNow =
      timeZone != null &&
      todayString != null &&
      mobileSelectedDate === todayString &&
      scrolledToNowForDateRef.current !== todayString &&
      mobileTimedEntries.length > 0;

    if (shouldScrollToNow) {
      const nowTopPx =
        ((minuteOfDayInTimeZone(scheduleNow(), timeZone) - mobileStartHour * 60) / 60) * mobileHourHeightPx;
      el.scrollTop = clampScrollTop(el, nowTopPx - el.clientHeight / 2);
      scrolledToNowForDateRef.current = todayString;
    } else if (selectedDateChanged) {
      el.scrollTop = 0;
    }

    lastMobileScrollDateRef.current = mobileSelectedDate;
    setMobileScrollEdge(getScrollEdge(el));
    // mobileGridNodeRef is a stable ref; it doesn't belong in deps. The combination of
    // height + selected-date + entry-count changes covers every case the ref node has mounted.
  }, [
    timeZone,
    todayString,
    mobileGridHeight,
    mobileSelectedDate,
    mobileTimedEntries.length,
    mobileStartHour,
    mobileHourHeightPx,
  ]);

  // Render — early empty case (no dated and no undated)
  if (datedGroups.length === 0 && !undatedGroup) return null;

  return (
    <div className={fillAvailable ? "flex min-h-0 flex-1 flex-col gap-3" : "flex flex-col gap-6"}>
      {/* SR-only live announcement of the currently visible page range / selected day */}
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {mobileGroupHeading || `Showing ${visibleRangeLabel}`}
      </span>

      {datedGroups.length > 0 && (
        <>
          {/* Mobile branch — single-day view with chip strip. In focused mode, this frame fills the
              parent and only the time-grid body scrolls vertically. Inline mode keeps its side
              gutter so page scrolling is still easy around the internal scroller. */}
          <div
            className={`${fillAvailable ? "flex min-h-0 flex-1 flex-col gap-2" : "mx-4 flex flex-col gap-3"} ${mobileBranchClass}`}
          >
            <div ref={mobileChromeRef} className={fillAvailable ? "flex shrink-0 flex-col gap-2" : "contents"}>
              <div
                className={
                  fillAvailable
                    ? "flex shrink-0 items-center gap-1 border-b border-gray-100 px-3 py-1.5"
                    : "flex items-center gap-1"
                }
              >
                {totalPages > 1 && (
                  <button
                    type="button"
                    onClick={handlePrev}
                    disabled={safePageIndex === 0}
                    className="flex h-11 w-11 shrink-0 touch-manipulation items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30"
                    aria-label="Previous days"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                )}
                <div
                  role="group"
                  aria-label="Select day"
                  className="flex flex-1 items-center justify-center gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                  style={{ scrollbarWidth: "none" }}
                >
                  {visibleGroups.map((group) => {
                    const derived = desktopByDate.get(group.date);
                    return (
                      <DayChip
                        key={group.date}
                        weekdayShort={derived?.weekdayShort ?? ""}
                        dayNumber={derived?.dayNumber ?? ""}
                        isActive={group.date === mobileSelectedDate}
                        activeColor={primaryColor}
                        activeTextColor={activeChipTextColor}
                        ariaLabel={`${derived?.weekday ?? ""}, ${derived?.dateShort ?? ""}`}
                        compact={fillAvailable}
                        onClick={() => setMobileSelectedDate(group.date)}
                      />
                    );
                  })}
                </div>
                {totalPages > 1 && (
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={safePageIndex >= totalPages - 1}
                    className="flex h-11 w-11 shrink-0 touch-manipulation items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30"
                    aria-label="Next days"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                )}
              </div>

              {mobileGroup && (
                <>
                  {/* Selected-day heading */}
                  <h3
                    className={
                      fillAvailable
                        ? "shrink-0 px-3 text-sm font-semibold leading-5 text-gray-900"
                        : "text-base font-semibold text-gray-900"
                    }
                  >
                    {mobileGroupHeading}
                  </h3>

                  {/* All-day row */}
                  {mobileAllDayItems.length > 0 && (
                    <div className="flex shrink-0 border-y border-gray-100 py-1.5">
                      {showTimeLabels && (
                        <div
                          style={{ width: HOUR_LABEL_WIDTH_PX }}
                          className="shrink-0 px-2 text-right text-xs font-medium text-gray-400"
                        >
                          All day
                        </div>
                      )}
                      <div className="flex flex-1 flex-col gap-1 px-1.5">
                        {mobileAllDayItems.map((item) => (
                          <CalendarEventCard
                            key={item.id}
                            item={item}
                            color={getFirstLabelColor(item, labelMap) ?? primaryColor}
                            onDelete={onDelete}
                            onItemClick={onItemClick}
                            timeZone={timeZone}
                            allDay
                            isRegistered={isItemRegistered(item)}
                            isWaitlisted={isItemWaitlisted(item)}
                            canSignUp={canItemSignUp(item)}
                            isSignupClosed={isItemSignupClosed(item)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {mobileGroup && (
              <div className={fillAvailable ? "flex min-h-0 flex-1 flex-col" : "contents"}>
                {/*
                  Single-day time grid — fixed/flex viewport with internal scroll. Rendered only
                  when the day has at least one timed item; all-day-only days would otherwise show
                  a tall empty bordered card. `touch-pan-y` blocks native pinch-zoom so the page
                  doesn't zoom; the `usePinchZoom` hook interprets two-finger pinch as an hour-row
                  scale (Google-Calendar-style). Top/bottom fades signal scrollability.
                */}
                {mobileTimedEntries.length > 0 && (
                  <div
                    ref={mobileViewportRef}
                    className={
                      fillAvailable
                        ? "relative min-h-0 flex-1 overflow-hidden border-y border-gray-200 bg-white"
                        : "relative mt-2 overflow-hidden rounded-lg border border-gray-200 bg-white"
                    }
                    style={fillAvailable ? undefined : { height: mobileViewportPx }}
                  >
                    <div
                      ref={mobileGridRef}
                      onScroll={handleMobileGridScroll}
                      className={`flex h-full touch-pan-y overflow-y-auto ${
                        fillAvailable ? "overscroll-y-contain pb-[max(1rem,env(safe-area-inset-bottom))]" : ""
                      }`}
                      style={fillAvailable ? { overscrollBehaviorY: "contain" } : undefined}
                    >
                      {showTimeLabels && (
                        <CalendarHourGutter
                          hours={mobileHours}
                          gridHeight={mobileGridHeight}
                          hourHeightPx={mobileHourHeightPx}
                        />
                      )}
                      <CalendarDayColumn
                        entries={mobileTimedEntries}
                        hours={mobileHours}
                        startHour={mobileStartHour}
                        gridHeight={mobileGridHeight}
                        hourHeightPx={mobileHourHeightPx}
                        labelMap={labelMap}
                        primaryColor={primaryColor}
                        onDelete={onDelete}
                        onItemClick={onItemClick}
                        timeZone={timeZone}
                        viewerRegistrations={viewerRegistrations}
                        date={mobileGroup.date}
                        showNowLine={mobileGroup.date === todayString}
                        showTimeLabels={showTimeLabels}
                      />
                    </div>
                    {/* Top fade — visible only when there's scrollable content above */}
                    <div
                      aria-hidden="true"
                      className={`pointer-events-none absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-white to-transparent transition-opacity duration-150 ${
                        mobileScrollEdge === "middle" || mobileScrollEdge === "bottom" ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    {/* Bottom fade — visible only when there's scrollable content below */}
                    <div
                      aria-hidden="true"
                      className={`pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-white to-transparent transition-opacity duration-150 ${
                        mobileScrollEdge === "top" || mobileScrollEdge === "middle" ? "opacity-100" : "opacity-0"
                      }`}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Desktop branch — week grid */}
          <div className={`hidden flex-col ${fillAvailable ? "min-h-0 flex-1 gap-2" : "gap-3"} ${desktopBranchClass}`}>
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={handlePrev}
                  disabled={safePageIndex === 0}
                  className="flex h-11 w-11 touch-manipulation items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
                  aria-label="Previous days"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm font-medium text-gray-700">{visibleRangeLabel}</span>
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={safePageIndex === totalPages - 1}
                  className="flex h-11 w-11 touch-manipulation items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
                  aria-label="Next days"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Day tabs — "All" shows the full week; selecting a day focuses just that day at full width */}
            <div role="group" aria-label="Select day" className="flex flex-wrap items-center justify-center gap-1.5">
              <button
                type="button"
                onClick={() => setDesktopSelectedDate(null)}
                aria-pressed={desktopActiveDate === null}
                className={`flex min-h-11 shrink-0 touch-manipulation items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                  desktopActiveDate === null ? "" : "text-gray-700 hover:bg-gray-100"
                }`}
                style={
                  desktopActiveDate === null ? { backgroundColor: primaryColor, color: activeChipTextColor } : undefined
                }
              >
                All
              </button>
              {visibleGroups.map((group) => {
                const derived = desktopByDate.get(group.date);
                return (
                  <DayChip
                    key={group.date}
                    weekdayShort={derived?.weekdayShort ?? ""}
                    dayNumber={derived?.dayNumber ?? ""}
                    isActive={group.date === desktopActiveDate}
                    activeColor={primaryColor}
                    activeTextColor={activeChipTextColor}
                    ariaLabel={`${derived?.weekday ?? ""}, ${derived?.dateShort ?? ""}`}
                    onClick={() => setDesktopSelectedDate(group.date)}
                  />
                );
              })}
            </div>

            {/*
              Scroll container — caps inline grids and fills focused grids, scrolling internally
              instead of stretching the page. Header row, all-day row, and time grid share this
              container, so the vertical scrollbar shrinks them equally and columns stay aligned.
            */}
            <div
              className={
                fillAvailable
                  ? "min-h-0 flex-1 overflow-y-auto rounded-lg border border-gray-200"
                  : "overflow-y-auto rounded-lg border border-gray-200"
              }
              style={fillAvailable ? undefined : { maxHeight: desktopMaxHeight }}
            >
              {/* Static header — day headers + all-day row scroll with the time grid. */}
              <div className="bg-white">
                {/* Day headers */}
                <div className="flex border-b border-gray-100">
                  {showTimeLabels && <div style={{ width: HOUR_LABEL_WIDTH_PX }} className="shrink-0" />}
                  {desktopRenderGroups.map((group, idx) => {
                    const derived = desktopByDate.get(group.date);
                    return (
                      <div
                        key={group.date}
                        className={`flex flex-1 flex-col gap-0.5 px-3 pb-3 pt-1 ${idx > 0 ? "border-l border-gray-100" : ""}`}
                      >
                        <p className="text-xs font-medium text-gray-500">{derived?.weekday}</p>
                        <p className="text-sm font-semibold text-gray-900">{derived?.dateShort}</p>
                      </div>
                    );
                  })}
                </div>

                {/* All-day row */}
                {hasAllDay && (
                  <div className="flex border-b border-gray-100">
                    {showTimeLabels && (
                      <div
                        style={{ width: HOUR_LABEL_WIDTH_PX }}
                        className="shrink-0 px-2 py-1.5 text-right text-xs font-medium text-gray-400"
                      >
                        All day
                      </div>
                    )}
                    {desktopRenderGroups.map((group, idx) => {
                      const derived = desktopByDate.get(group.date);
                      return (
                        <div
                          key={group.date}
                          className={`flex flex-1 flex-col gap-1 px-1.5 py-1.5 ${idx > 0 ? "border-l border-gray-100" : ""}`}
                        >
                          {derived?.allDayItems.map((item) => (
                            <CalendarEventCard
                              key={item.id}
                              item={item}
                              color={getFirstLabelColor(item, labelMap) ?? primaryColor}
                              onDelete={onDelete}
                              onItemClick={onItemClick}
                              timeZone={timeZone}
                              allDay
                              isRegistered={isItemRegistered(item)}
                              isWaitlisted={isItemWaitlisted(item)}
                              canSignUp={canItemSignUp(item)}
                              isSignupClosed={isItemSignupClosed(item)}
                            />
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Time grid */}
              <div className="flex">
                {showTimeLabels && (
                  <CalendarHourGutter
                    hours={hours}
                    gridHeight={gridHeight}
                    hourHeightPx={SCHEDULE_CALENDAR_DESKTOP_HOUR_HEIGHT_PX}
                  />
                )}
                {desktopRenderGroups.map((group, idx) => {
                  const derived = desktopByDate.get(group.date);
                  return (
                    <CalendarDayColumn
                      key={group.date}
                      entries={derived?.timedEntries ?? []}
                      hours={hours}
                      startHour={startHour}
                      gridHeight={gridHeight}
                      hourHeightPx={SCHEDULE_CALENDAR_DESKTOP_HOUR_HEIGHT_PX}
                      labelMap={labelMap}
                      primaryColor={primaryColor}
                      onDelete={onDelete}
                      onItemClick={onItemClick}
                      timeZone={timeZone}
                      className={idx > 0 ? "border-l border-gray-100" : ""}
                      viewerRegistrations={viewerRegistrations}
                      date={group.date}
                      showNowLine={group.date === todayString}
                      showTimeLabels={showTimeLabels}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Unscheduled section */}
      {undatedGroup && undatedGroup.items.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-medium text-gray-700">Unscheduled</h3>
          <div className={`grid gap-2 ${unscheduledGridClass}`}>
            {undatedGroup.items.map((item) => (
              <div key={item.id} className="min-h-[60px]">
                <CalendarEventCard
                  item={item}
                  color={getFirstLabelColor(item, labelMap) ?? primaryColor}
                  onDelete={onDelete}
                  onItemClick={onItemClick}
                  timeZone={timeZone}
                  allDay
                  isRegistered={isItemRegistered(item)}
                  isWaitlisted={isItemWaitlisted(item)}
                  canSignUp={canItemSignUp(item)}
                  isSignupClosed={isItemSignupClosed(item)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
